<template>
    <div
        id="autocomplete"
        ref="autocompleteRef"
        class="relative flex rounded-full bg-[#f5f4f6] dark:bg-surface-card w-full max-w-[483px] mx-auto"
        :class="{
            active: searchActive || term,
            'shadow-highlight': hasTerm
        }"
    >
        <div
            class="relative flex w-full items-center rounded-full transition-shadow duration-200"
            :class="{
                'rounded-b-none': hasDropdownVisible
            }"
        >
            <input
                ref="input"
                v-model="term"
                class="h-[52px] w-full text-ellipsis bg-transparent pl-[18px] pr-3 align-top outline-none transition-all duration-200 placeholder:text-[#a495aa] dark:placeholder:text-txt-body text-gray-900 dark:text-white text-base"
                placeholder="Hľadaj vo všetkých príležitostiach..."
                type="text"
                aria-label="Hľadať na GROWNi"
                @focus="showResults"
                @keydown.down.prevent="moveCursor(1)"
                @keydown.esc="handleEscKey"
                @keydown.tab="hideResults"
                @keydown.up.prevent="moveCursor(-1)"
                @keypress.enter="submitSearch"
            />

            <div class="absolute inset-y-0 right-0 flex items-center gap-2 pr-[6px]">
                <!-- Location dropdown button -->
                <button
                    type="button"
                    class="flex items-center gap-1.5 px-2 py-2 rounded-full hover:bg-[#edebef] dark:hover:bg-subtle transition-colors"
                    :aria-label="`Hľadať v: ${selectOptions[selectedOption].label}`"
                    aria-haspopup="true"
                    :aria-expanded="isLocationDropdownOpen"
                    @click="toggleLocationDropdown"
                >
                    <span class="text-sm font-semibold text-[#6c5d73] dark:text-txt-body leading-5">
                        {{ selectOptions[selectedOption].label }}
                    </span>
                    <CIcon name="chevron-down" class="text-[#6c5d73] dark:text-txt-body" :size="20" />
                </button>

                <!-- Search button -->
                <button
                    type="button"
                    class="flex items-center justify-center w-10 h-10 rounded-full bg-[#edebef] dark:bg-subtle border border-[#edebef] dark:border-default hover:bg-gray-300 dark:hover:bg-subtle transition-colors shrink-0"
                    aria-label="Hľadať"
                    @click="submitSearch"
                >
                    <CIcon name="search" class="text-[#a495aa] dark:text-txt-body" :size="20" />
                </button>
            </div>
        </div>

        <!-- Location dropdown menu -->
        <div v-if="isLocationDropdownOpen" class="absolute top-full right-0 z-50 mt-2 w-64 rounded-lg bg-white dark:bg-surface-card shadow-lg border border-gray-200 dark:border-default">
            <div class="py-2">
                <button
                    v-for="option in Object.values(selectOptions)"
                    :key="option.id"
                    class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-subtle transition-colors"
                    :class="{
                        'bg-gray-100 dark:bg-subtle font-semibold': selectedOption === option.id
                    }"
                    @click="selectLocation(option.id)"
                >
                    {{ option.label }}
                </button>
            </div>
        </div>

        <div v-if="hasDropdownVisible" class="absolute top-full z-40 w-full max-w-full md:w-screen mt-1">
            <div
                class="_dropdown w-full overflow-hidden rounded-lg border border-gray-300 dark:border-default bg-white dark:bg-surface-card shadow-highlight dark:shadow-gray-900/50"
            >
                <div
                    class="fancy-scrollbar mr-2 max-h-80 overflow-auto"
                    :class="{
                        'pt-2': suggestions.length
                    }"
                >
                    <div
                        v-for="(suggestion, index) in suggestions"
                        :key="index"
                        class="_suggestion flex min-h-[41px] cursor-pointer items-center space-x-4 py-2 pl-4 pr-1.5 hover:bg-gray-200 dark:hover:bg-subtle transition-colors text-gray-900 dark:text-white"
                        :class="{ 'bg-gray-200 dark:bg-subtle': cursorIndex === index }"
                        @click="openSuggestion(suggestion)"
                    >
                        <div class="flex w-full flex-row items-center justify-start">
                            <div class="flex size-8 shrink-0 items-center justify-center">
                                <CIcon
                                    v-if="!suggestion.image || suggestion.imageError"
                                    class="custom-icon"
                                    :name="getIcon(suggestion.type)"
                                    :size="24"
                                />
                                <img
                                    v-else
                                    :alt="cleanTitle(suggestion.text)"
                                    class="size-8 rounded-full object-cover"
                                    :src="
                                        suggestion.miniatureImage
                                            ? getImageMiniature(suggestion.image)
                                            : suggestion.image
                                    "
                                    @error="suggestion.imageError = true"
                                />
                            </div>

                            <div class="ml-2 truncate whitespace-nowrap">
                                <span v-html="suggestion.text"></span>
                            </div>

                            <div
                                class="mb-[0.2rem] ml-2 self-end align-bottom text-xs font-normal capitalize text-gray-500 dark:text-txt-body"
                            >
                                {{ getText(suggestion.type) }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="rounded-tr-0 cursor-pointer rounded-b-2xl bg-white dark:bg-surface-card border-t border-gray-200 dark:border-default">
                    <div
                        class="flex w-full flex-row items-center justify-start py-2 pl-3 pr-1.5 hover:bg-gray-200 dark:hover:bg-subtle transition-colors"
                        @click="searchPhrase"
                    >
                        <div
                            class="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-500 dark:bg-primary-600"
                        >
                            <CIcon class="text-white" name="search" :size="24" />
                        </div>

                        <div class="ml-2 truncate whitespace-nowrap text-primary-500 dark:text-primary-400">
                            <span class="font-normal">Vyhľadať frázu </span>
                            <span class="font-semibold">"{{ term }}"</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { LoadingState } from '#shared/types/LoadingState'

import { Collections } from '#shared/constants/collections'
import CFormSelect from '@/components/ui/CFormSelect.vue'
import CIcon from '@/components/ui/CIcon.vue'
import { useImage } from '@/composables/image/useImage'
import { useMergedOpportunities } from '@/composables/mergedOpportunities/useMergedOpportunities'
import { onClickOutside } from '@vueuse/core'
import debounce from 'lodash-es/debounce'
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const { generateImage, Variant } = useImage()
const { searchMergedOpportunities } = useMergedOpportunities()

const MIN_SEARCHABLE_LENGTH = 2
const HITS_PER_PAGE = 30
const RULE_CONTEXT_NAME = 'search-results'
const INDEXES = [
    Collections.JOBS,
    Collections.MENTORS,
    Collections.ORGS,
    Collections.SKILLS,
    Collections.PROJECTS,
    Collections.VOLUNTEERS,
    Collections.SEARCHABLE_PAGES_DATA,
    Collections.BLOGS
] as const

type TAllowedIndexes = (typeof INDEXES)[number]

type Suggestion = {
    text: string
    type: TAllowedIndexes
    path: string
    image?: string
    miniatureImage?: boolean
    imageError?: boolean
}

const SEARCH_PROVIDERS: Record<TAllowedIndexes, (hit: any) => Suggestion> = {
    [Collections.SKILLS]: (hit) => ({
        text: hit._highlightResult?.title?.value || hit.title || '',
        type: Collections.SKILLS,
        path: `/vzdelavanie/${hit.slug || hit.id}`,
        image: hit.image || null,
        miniatureImage: true
    }),

    [Collections.JOBS]: (hit) => ({
        text: hit._highlightResult?.title?.value || hit.title || '',
        type: Collections.JOBS,
        path: `/praca/${hit.slug || hit.id}`,
        image: hit.orgImage || hit.orgMeta?.image || null,
        miniatureImage: true
    }),

    [Collections.PROJECTS]: (hit) => ({
        text: hit._highlightResult?.title?.value || hit.title || '',
        type: Collections.PROJECTS,
        path: `/dobrovolnictvo/dobrovolnicke-projekty/${hit.slug || hit.id}`,
        image: hit.image || hit.orgImage || hit.orgMeta?.image || null,
        miniatureImage: true
    }),

    [Collections.MENTORS]: (hit) => ({
        text: [hit._highlightResult?.firstName?.value || hit.firstName || '', hit._highlightResult?.lastName?.value || hit.lastName || ''].join(' ').trim(),
        type: Collections.MENTORS,
        path: `/mentor/${hit.slug || hit.id}${hit.id && hit.slug ? `?uid=${hit.id}` : ''}`,
        image: hit.profileImg || null,
        miniatureImage: true
    }),

    [Collections.VOLUNTEERS]: (hit) => ({
        text: [hit._highlightResult?.firstName?.value || hit.firstName || '', hit._highlightResult?.lastName?.value || hit.lastName || ''].join(' ').trim(),
        type: Collections.VOLUNTEERS,
        path: `/dobrovolnictvo/dobrovolnici/${hit.slug || hit.id}`,
        image: hit.profileImg || null,
        miniatureImage: true
    }),

    [Collections.ORGS]: (hit) => ({
        text: hit._highlightResult?.title?.value || hit.title || '',
        type: Collections.ORGS,
        path: `/orgs/${hit.slug || hit.id}`,
        image: hit.image || null,
        miniatureImage: true
    }),

    [Collections.SEARCHABLE_PAGES_DATA]: (hit) => ({
        text: hit._highlightResult?.title?.value || hit.title || '',
        type: Collections.SEARCHABLE_PAGES_DATA,
        path: hit.path,
        image: undefined,
        miniatureImage: false
    }),

    [Collections.BLOGS]: (hit) => ({
        text: hit._highlightResult?.title?.value || hit.title || '',
        type: Collections.BLOGS,
        path: `/blog/${hit.slug || hit.id}`,
        image: undefined,
        miniatureImage: false
    })
}

type TOptionType<TId> = {
    label: string
    urlSlug: string
    placeholderText: string
    id: TId
}

const SELECT_OPTIONS: Record<'all', TOptionType<'all'>> & Record<TAllowedIndexes, TOptionType<TAllowedIndexes>> = {
    'all': {
        label: 'Všade',
        urlSlug: 'vysledky-vyhladavania',
        placeholderText: 'vo všetkých príležitostiach',
        id: 'all'
    },
    [Collections.JOBS]: {
        label: 'Práca a stáže',
        urlSlug: 'praca',
        placeholderText: 'prácu, stáž',
        id: Collections.JOBS
    },
    [Collections.MENTORS]: {
        label: 'Mentori',
        urlSlug: 'mentori',
        placeholderText: 'mentora',
        id: Collections.MENTORS
    },
    [Collections.PROJECTS]: {
        label: 'Dobro projekty',
        urlSlug: 'dobrovolnictvo-dobrovolnicke-projekty',
        placeholderText: 'dobrovoľnícky projekt, príležitosť',
        id: Collections.PROJECTS
    },
    [Collections.VOLUNTEERS]: {
        label: 'Dobrovoľníci',
        urlSlug: 'dobrovolnictvo-dobrovolnici',
        placeholderText: 'dobrovoľníka',
        id: Collections.VOLUNTEERS
    },
    [Collections.SKILLS]: {
        label: 'Vzdelávanie',
        urlSlug: 'vzdelavanie',
        placeholderText: 'video, event',
        id: Collections.SKILLS
    },
    [Collections.ORGS]: {
        label: 'Organizácie',
        urlSlug: 'organizacie',
        placeholderText: 'firmu, neziskovku, iniciatívu',
        id: Collections.ORGS
    },
    [Collections.SEARCHABLE_PAGES_DATA]: {
        label: 'Stránky',
        urlSlug: 'vysledky-vyhladavania',
        placeholderText: 'stránku',
        id: Collections.SEARCHABLE_PAGES_DATA
    },
    [Collections.BLOGS]: {
        label: 'Blogy',
        urlSlug: 'blog',
        placeholderText: 'článok',
        id: Collections.BLOGS
    }
}

const router = useRouter()
const route = useRoute()

const state = ref<LoadingState>('idle')
const suggestions = ref<Suggestion[]>([])
const selectOptions = SELECT_OPTIONS
const selectedOption = ref<TAllowedIndexes | 'all'>('all')
const term = ref<string>('')
const isOpen = ref<boolean>(false)
const cursorIndex = ref<number>(-1)
const searchActive = ref<boolean>(false)
const input = ref<HTMLInputElement>()
const isLocationDropdownOpen = ref<boolean>(false)

const hasTerm = computed(() => term.value.trim().length > 0)
const hasSuggestions = computed(() => suggestions.value.length > 0)
const hasDropdownVisible = computed(() => isOpen.value && hasTerm.value)
const placeholder = computed(() => {
    const selectedOptionData = selectOptions[selectedOption.value]
    return `Vyhľadaj ${selectedOptionData?.placeholderText} ...`
})
const selectedSuggestion = computed(() => suggestions.value[cursorIndex.value])

const searchDebounced = debounce(() => search(), 400)

watch(term, () => {
    searchDebounced()
})

watch(
    () => route.name,
    (name) => {
        if (term.value) return
        const option = getOptionDataByRoute(name as string)
        selectedOption.value = option.id
    },
    { immediate: true }
)

watch(
    selectedOption,
    (newValue) => {
        if (newValue) {
            updateSelection(newValue)
        }
    },
    { immediate: true }
)

onMounted(() => {
    if (route.query.q) {
        term.value = route.query.q as string
    }
})

const autocompleteRef = ref<HTMLElement | null>(null)

onClickOutside(autocompleteRef, () => {
    hideResults()
    isLocationDropdownOpen.value = false
})

async function updateSelection(option: TAllowedIndexes | 'all') {
    selectedOption.value = option
    if (term.value) {
        await search()
    }
}

function getText(type: Suggestion['type']): string {
    switch (type) {
        case Collections.ORGS:
            return 'Organizácia'
        case Collections.VOLUNTEERS:
            return 'Dobrovoľník'
        case Collections.MENTORS:
            return 'Mentor'
        case Collections.PROJECTS:
            return 'Dobrovoľníctvo'
        case Collections.JOBS:
            return 'Práca'
        case Collections.SKILLS:
            return 'Vzdelávanie'
        case Collections.SEARCHABLE_PAGES_DATA:
            return 'Stránka'
        case Collections.BLOGS:
            return 'Blog'
        default:
            return ''
    }
}

function getIcon(type: Suggestion['type']): string {
    switch (type) {
        case Collections.ORGS:
            return 'my-organization'
        case Collections.VOLUNTEERS:
            return 'my-profile'
        case Collections.MENTORS:
            return 'mentoring'
        case Collections.PROJECTS:
            return 'volunteering'
        case Collections.JOBS:
            return 'jobs'
        case Collections.SKILLS:
            return 'skills'
        case Collections.SEARCHABLE_PAGES_DATA:
            return 'globe'
        case Collections.BLOGS:
            return 'quote-outline'
        default:
            return ''
    }
}

async function search() {
    isOpen.value = true
    const searchTerm = term.value.trim()

    if (searchTerm.length < MIN_SEARCHABLE_LENGTH) {
        suggestions.value = []
        return
    }

    if (selectedOption.value !== 'all' && !INDEXES.includes(selectedOption.value)) {
        suggestions.value = []
        return
    }

    state.value = 'loading'

    try {
        const searchResults = await searchMergedOpportunities(searchTerm, {
            hitsPerPage: HITS_PER_PAGE,
            filters: selectedOption.value !== 'all' ? `_index:${selectedOption.value}` : undefined,
            ruleContexts: [RULE_CONTEXT_NAME]
        })

        const newSuggestions = searchResults.hits
            .filter((item) => item._index && SEARCH_PROVIDERS[item._index as TAllowedIndexes])
            .map((item) => {
                const provider = SEARCH_PROVIDERS[item._index as TAllowedIndexes]
                return provider(item)
            })

        suggestions.value = newSuggestions
        state.value = 'done'
    } catch (error) {
        console.error('Error during search:', error)
        state.value = 'error'
        suggestions.value = []
    }
    // No reopening here: search() already opened the dropdown, and if Esc, Enter or a click outside
    // closed it while the request was running, it stays closed.
}

function submitSearch() {
    if (isOpen.value && selectedSuggestion.value) {
        return openSuggestion(selectedSuggestion.value)
    }

    const option = getOptionData(selectedOption.value)

    router.push({
        name: option.urlSlug,
        query: { q: term.value.trim() }
    })

    hideResults()
}

function onCloseSearch() {
    if (!term.value) return

    clearSearch()
}

function searchPhrase() {
    const option = getOptionData(selectedOption.value)

    router.push({
        name: option.urlSlug,
        query: { q: term.value }
    })

    hideResults()
}

function handleEscKey() {
    if (isOpen.value) hideResults()
    else input.value?.blur()
}

function getOptionDataByRoute(routeName: string | null | undefined) {
    if (!routeName) return selectOptions.all
    const iterableOptions = Object.values(selectOptions)
    const selected = iterableOptions.find((item) => item.urlSlug === routeName)
    return selected ?? selectOptions.all
}

function getOptionData(selectedOption?: TAllowedIndexes | 'all') {
    if (!selectedOption) return selectOptions.all
    return selectOptions[selectedOption] ?? selectOptions.all
}

function clearSearch() {
    term.value = ''
}

// Reopens the suggestions already loaded for the current term (on focus, arrow down). Doesn't search.
function showResults() {
    if (!term.value) {
        return
    }
    isOpen.value = true
}

function hideResults() {
    cursorIndex.value = -1
    isOpen.value = false
}

function moveCursor(step: -1 | 1) {
    if (step === 1 && !isOpen.value && hasSuggestions.value) {
        showResults()
        return
    }

    const minIndex = -1
    const maxIndex = suggestions.value.length - 1
    const newCursorIndex = cursorIndex.value + step

    cursorIndex.value = Math.min(Math.max(minIndex, newCursorIndex), maxIndex)
}

function openSuggestion(suggestion: Suggestion) {
    hideResults()
    clearSearch()

    router.push(suggestion.path)
}

function cleanTitle(title: string) {
    return title.replace(/<\/?[^>]+(>|$)/g, '')
}

function getImageMiniature(url: string) {
    return generateImage(url, Variant.A)
}

function toggleLocationDropdown() {
    isLocationDropdownOpen.value = !isLocationDropdownOpen.value
}

function selectLocation(option: TAllowedIndexes | 'all') {
    selectedOption.value = option
    isLocationDropdownOpen.value = false
    if (term.value) {
        search()
    }
}
</script>
